import type { GoalLevel } from "../types";

const LEVEL_UA: Record<GoalLevel, string> = {
  year: "Рік",
  month: "Місяць",
  week: "Тиждень",
};

const MONTHS_UA = [
  "січень",
  "лютий",
  "березень",
  "квітень",
  "травень",
  "червень",
  "липень",
  "серпень",
  "вересень",
  "жовтень",
  "листопад",
  "грудень",
];

/** Genitive, because a date range reads "10 серпня", not "10 серпень". */
const MONTHS_GEN = [
  "січня",
  "лютого",
  "березня",
  "квітня",
  "травня",
  "червня",
  "липня",
  "серпня",
  "вересня",
  "жовтня",
  "листопада",
  "грудня",
];

/**
 * A week as the dates it covers: "10–16 серпня".
 *
 * "тиждень 33" is a number you have to look up before it means anything —
 * nobody knows which days that is without a calendar. The year is dropped
 * while it is the current one, and only the crossing cases spell out both
 * months.
 */
function weekRangeLabel(from: Date, to: Date): string {
  const day = (d: Date) => d.getUTCDate();
  const month = (d: Date) => MONTHS_GEN[d.getUTCMonth()];
  const fromYear = from.getUTCFullYear();
  const toYear = to.getUTCFullYear();
  const tail = toYear === new Date().getFullYear() ? "" : ` ${toYear}`;

  if (fromYear !== toYear) {
    return `${day(from)} ${month(from)} ${fromYear} – ${day(to)} ${month(to)} ${toYear}`;
  }
  if (from.getUTCMonth() === to.getUTCMonth()) {
    return `${day(from)}–${day(to)} ${month(to)}${tail}`;
  }
  return `${day(from)} ${month(from)} – ${day(to)} ${month(to)}${tail}`;
}

/** "2026-08" -> "серпень 2026", "2026-W33" -> "10–16 серпня". */
export function periodLabel(level: GoalLevel, key: string): string {
  if (level === "year") return key;
  if (level === "month") {
    const [year, month] = key.split("-");
    const name = MONTHS_UA[Number(month) - 1];
    return name ? `${name} ${year}` : key;
  }
  const [yearText, weekText] = key.toUpperCase().split("-W");
  const year = Number(yearText);
  const week = Number(weekText);
  if (!Number.isFinite(year) || !Number.isFinite(week) || !weekText) return key;
  const monday = isoWeekMonday(year, week);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return weekRangeLabel(monday, sunday);
}

/** "тиждень 33" — kept as a hover hint, since the label no longer says it. */
export function weekNumberHint(level: GoalLevel, key: string): string | undefined {
  if (level !== "week") return undefined;
  const week = Number(key.toUpperCase().split("-W")[1]);
  return Number.isFinite(week) ? `тиждень ${week}` : undefined;
}

/** Monday of an ISO week, the one date JS cannot produce without arithmetic. */
function isoWeekMonday(year: number, week: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const shift = (jan4.getUTCDay() + 6) % 7; // Monday = 0
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - shift + (week - 1) * 7);
  return monday;
}

/** Whole days left in the period, or null when the key cannot be parsed. */
export function daysLeftIn(level: GoalLevel, key: string): number | null {
  let end: Date;
  if (level === "year") {
    const year = Number(key);
    if (!Number.isFinite(year)) return null;
    end = new Date(Date.UTC(year, 11, 31));
  } else if (level === "month") {
    const [year, month] = key.split("-").map(Number);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
    end = new Date(Date.UTC(year, month, 0)); // day 0 of next month = last of this
  } else {
    const [year, week] = key.toUpperCase().split("-W").map(Number);
    if (!Number.isFinite(year) || !Number.isFinite(week)) return null;
    end = isoWeekMonday(year, week);
    end.setUTCDate(end.getUTCDate() + 6);
  }
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((end.getTime() - today) / 86_400_000));
}

interface PeriodSwitcherProps {
  level: GoalLevel;
  onLevel: (level: GoalLevel) => void;
  /** Known keys for the active level, newest first. */
  periods: string[];
  value: string;
  onValue: (key: string) => void;
  /** The key for "now" at this level, so the reset button knows where to go. */
  currentKey: string;
}

/**
 * Level pills plus a stepper through the periods that actually hold goals.
 *
 * A date picker would offer every week since 2026 including the 40 that never
 * had a goal in them. The backend returns only the keys with content (plus the
 * current one), so stepping is both shorter and honest about what exists.
 */
export default function PeriodSwitcher({
  level,
  onLevel,
  periods,
  value,
  onValue,
  currentKey,
}: PeriodSwitcherProps) {
  const index = periods.indexOf(value);
  // Newest first, so "older" is forward in the array and "newer" is backward.
  const older = index >= 0 && index < periods.length - 1 ? periods[index + 1] : null;
  const newer = index > 0 ? periods[index - 1] : null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="radiogroup"
        aria-label="Рівень цілей"
        className="flex gap-1 rounded-control bg-surface-2 p-0.5"
      >
        {(Object.keys(LEVEL_UA) as GoalLevel[]).map((item) => (
          <button
            key={item}
            type="button"
            role="radio"
            aria-checked={level === item}
            onClick={() => onLevel(item)}
            className={`rounded-control px-3 py-1.5 text-xs font-medium transition-colors ${
              level === item
                ? "bg-surface text-fg shadow-1"
                : "text-muted hover:text-fg"
            }`}
          >
            {LEVEL_UA[item]}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => older && onValue(older)}
          disabled={!older}
          aria-label="Попередній період"
          className="jv-btn px-2"
        >
          ‹
        </button>
        <span
          title={weekNumberHint(level, value)}
          className="min-w-36 text-center text-xs font-medium whitespace-nowrap text-fg"
        >
          {periodLabel(level, value)}
        </span>
        <button
          type="button"
          onClick={() => newer && onValue(newer)}
          disabled={!newer}
          aria-label="Наступний період"
          className="jv-btn px-2"
        >
          ›
        </button>
      </div>

      {value !== currentKey && (
        <button type="button" onClick={() => onValue(currentKey)} className="jv-btn">
          Зараз
        </button>
      )}
    </div>
  );
}
